import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { scoreColor } from "@/lib/helpers";
import { toast } from "sonner";
import { Smartphone, Monitor, Zap, HelpCircle } from "lucide-react";

const LAB_LABELS = { lcp: "LCP", fcp: "FCP", cls: "CLS", tbt: "TBT", speed_index: "Speed Index", tti: "Interactif" };
const CAT_LABELS = { performance: "Performance", accessibility: "Accessibilité", "best-practices": "Bonnes pratiques", seo: "SEO" };
const FIELD_CAT = { FAST: { l: "Rapide", c: "text-emerald-600" }, AVERAGE: { l: "Moyen", c: "text-yellow-600" }, SLOW: { l: "Lent", c: "text-red-500" } };

function Ring({ score }) {
  const col = score == null ? "#94a3b8" : score >= 90 ? "#10b981" : score >= 50 ? "#f59e0b" : "#ef4444";
  return (
    <div className="relative w-16 h-16 shrink-0">
      <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
        <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e2e8f0" strokeWidth="3" />
        <circle cx="18" cy="18" r="15.5" fill="none" stroke={col} strokeWidth="3" strokeDasharray={`${score ?? 0} 100`} strokeLinecap="round" />
      </svg>
      <span className="absolute inset-0 grid place-items-center font-heading font-bold text-sm" style={{ color: col }}>{score ?? "—"}</span>
    </div>
  );
}

export function PageSpeedCard({ establishment }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("mobile");
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/establishments/${establishment.id}/pagespeed`);
    setData(data);
  }, [establishment.id]);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setRunning(true);
    try {
      await api.post(`/establishments/${establishment.id}/pagespeed`);
      toast.success("Analyse PageSpeed terminée (données Google)");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const r = data?.latest?.[tab];
  return (
    <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm" data-testid="pagespeed-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><Zap className="w-5 h-5 text-amber-500" /> Performance Google PageSpeed</h2>
          <p className="text-xs text-slate-500 mt-0.5">Mesures réelles fournies par Google PageSpeed Insights (Lighthouse + Core Web Vitals).</p>
        </div>
        <Button onClick={run} disabled={running || !data?.can_run} data-testid="run-pagespeed-button" className="bg-amber-500 hover:bg-amber-600 rounded-xl">
          <Zap className={`w-4 h-4 mr-2 ${running ? "animate-pulse" : ""}`} /> {running ? "Analyse Google en cours…" : data?.available ? "Relancer l'analyse" : "Lancer l'analyse PageSpeed"}
        </Button>
      </div>

      {data && !data.can_run && (
        <p className="text-sm text-slate-500 flex items-center gap-2" data-testid="pagespeed-unavailable"><HelpCircle className="w-4 h-4" />
          {establishment.is_demo ? "Analyse PageSpeed non disponible sur l'établissement de démonstration." : "Donnée indisponible — renseignez l'URL du site internet de l'établissement."}</p>
      )}
      {data?.can_run && !data.available && (
        <p className="text-sm text-slate-500 flex items-center gap-2" data-testid="pagespeed-empty"><HelpCircle className="w-4 h-4" /> Donnée indisponible — aucune analyse PageSpeed n'a encore été lancée.</p>
      )}

      {r && (
        <>
          <div className="flex gap-2 mb-4">
            {[["mobile", Smartphone, "Mobile"], ["desktop", Monitor, "Ordinateur"]].map(([k, Icon, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`pagespeed-tab-${k}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                <Icon className="w-4 h-4" /> {l}
              </button>
            ))}
            <span className="ml-auto text-xs text-slate-400 self-center">Analysé le {new Date(data.latest.created_at).toLocaleString("fr-FR")}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.entries(CAT_LABELS).map(([k, l]) => (
              <div key={k} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50" data-testid={`pagespeed-score-${k}`}>
                <Ring score={r.scores[k]} />
                <span className="text-sm font-medium text-slate-700">{l}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {Object.entries(LAB_LABELS).map(([k, l]) => (
              <div key={k} className="p-2.5 rounded-lg border border-slate-100" data-testid={`pagespeed-lab-${k}`}>
                <div className="text-[11px] uppercase tracking-wide text-slate-400">{l}</div>
                <div className={`font-mono text-sm font-semibold ${scoreColor(r.lab[k]?.score)}`}>{r.lab[k]?.display ?? "—"}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-sm">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400 mr-2">Données terrain (utilisateurs réels, CrUX) :</span>
            {r.field ? (
              <span className={FIELD_CAT[r.field.overall]?.c || "text-slate-600"} data-testid="pagespeed-field">
                {FIELD_CAT[r.field.overall]?.l || r.field.overall}
                {Object.entries(r.field.metrics || {}).map(([k, m]) => ` · ${k.toUpperCase()} ${m.percentile}${k === "cls" ? "" : " ms"} (${FIELD_CAT[m.category]?.l || m.category})`)}
              </span>
            ) : <span className="text-slate-400" data-testid="pagespeed-field-unavailable">Donnée indisponible — trafic insuffisant pour ce site dans Chrome UX Report.</span>}
          </div>
          {r.opportunities?.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Pistes d'amélioration identifiées par Google</div>
              <ul className="space-y-1.5">
                {r.opportunities.slice(0, 5).map((o) => (
                  <li key={o.id} className="text-sm text-slate-700 flex items-start gap-2" data-testid={`pagespeed-opp-${o.id}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 shrink-0" />
                    <span>{o.title}{o.display ? <span className="text-slate-400"> — {o.display}</span> : null}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
